'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowUp, Loader2, Mic, PenLine, Square, Wand2, X } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { GroupedSourcesPanel, type GroupedSource } from '@/app/dashboard/components/GroupedSourcesPanel';
import { AddSourceDialog } from '@/components/features/sources/AddSourceDialog';
import { useToast } from '@/hooks/use-toast';
import { useResumeBuilderStore } from '@/stores/resume-builder-store';
import { normalizeTextValue } from '@/lib/resume/normalize-content';
import { compareResumeField, type ResumeChangeItem } from '@/lib/resume/compare';
import { ResumeComparisonDialog } from '@/components/features/resume/ResumeComparisonDialog';
import { useResumeImprove } from '@/lib/resume/agent/improve/useResumeImprove';
import { ResumeImproveProgress } from '@/lib/resume/agent/improve/ResumeImproveProgress';
import { RefineJobSourceDialog } from '@/components/features/resume/RefineJobSourceDialog';

type ImproveField = 'summary' | 'responsibilities' | 'custom';

/** Where each improvable field sits in the resume, for the comparison list. */
const IMPROVE_FIELD_SECTIONS: Record<ImproveField, string> = {
  summary: 'profile',
  responsibilities: 'employment',
  custom: 'customSections',
};

interface ImproveTextControlProps {
  text: unknown;
  field: ImproveField;
  onImproved: (improvedText: string) => void;
  instructionPlaceholder?: string;
  context?: {
    jobTitle?: string;
    company?: string;
  };
  /** True when the builder is running without authentication. Refine and
   *  Improve both need a server-side resume, so guests are gated at the
   *  button rather than left to hit a failing API call. */
  guestMode?: boolean;
  /** Guest-mode click handler for Refine/Improve — shows the sign-in dialog
   *  (see `withGuestGate` in `ResumeBuilderClient`) instead of running the
   *  action. Required when `guestMode` is true. */
  onGuestAction?: () => void;
}

export default function ImproveTextControl({
  text,
  field,
  onImproved,
  instructionPlaceholder,
  context,
  guestMode,
  onGuestAction,
}: ImproveTextControlProps) {
  const { toast } = useToast();
  const resumeId = useResumeBuilderStore((state) => state.resume?.id);

  /**
   * IMPROVE now runs the five-step resume improve agent and streams its
   * progress. The checklist is the point, not decoration: the steps that matter
   * most to a user are the ones about what the agent refused to do — "no
   * figures available, so none will be added" is the difference between a
   * resume they can defend in an interview and one they cannot.
   */
  const improve = useResumeImprove();
  const isImproving = improve.status === 'running';
  // Pulled out because the hook returns a fresh object each render; only the
  // callbacks are stable, so effects must depend on those and not on `improve`.
  const { reset: resetImprove, run: runImprove } = improve;

  const [isRefining, setIsRefining] = useState(false);
  /**
   * Improve replaces the field's text in place, so the previous wording is gone
   * from the editor the moment it lands. Holding the diff here is what lets the
   * user read what actually changed before deciding to keep it.
   */
  const [improveChanges, setImproveChanges] = useState<ResumeChangeItem[]>([]);
  const [showAiInput, setShowAiInput] = useState(false);
  const [showRefineDialog, setShowRefineDialog] = useState(false);
  const [showAddSourceDialog, setShowAddSourceDialog] = useState(false);
  const [sources, setSources] = useState<GroupedSource[]>([]);
  const [loadingSources, setLoadingSources] = useState(false);
  const [isClearingSources, setIsClearingSources] = useState(false);
  const [instructionInput, setInstructionInput] = useState('');
  const [isInstructionVoiceRecording, setIsInstructionVoiceRecording] = useState(false);
  const [isInstructionVoiceTranscribing, setIsInstructionVoiceTranscribing] = useState(false);
  const [instructionVoiceError, setInstructionVoiceError] = useState<string | null>(null);
  const safeText = normalizeTextValue(text);
  const improveComparisonLabel =
    field === 'summary'
      ? 'Professional summary'
      : field === 'responsibilities'
        ? `Responsibilities: ${context?.jobTitle || 'Role'}${context?.company ? ` at ${context.company}` : ''}`
        : 'Custom section';
  const containerRef = useRef<HTMLDivElement>(null);
  const instructionInputRef = useRef<HTMLInputElement>(null);
  const wasAiInputOpenRef = useRef(false);
  const instructionVoiceRecorderRef = useRef<MediaRecorder | null>(null);
  const instructionVoiceStreamRef = useRef<MediaStream | null>(null);
  const instructionVoiceChunksRef = useRef<Blob[]>([]);
  const cancelInstructionVoiceRef = useRef(false);
  const submitAfterVoiceRef = useRef(false);

  const refreshSources = useCallback(async () => {
    if (!resumeId) {
      setSources([]);
      return;
    }

    setLoadingSources(true);
    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/sources`);
      if (response.ok) {
        const data = await response.json();
        setSources(data.sources || []);
      }
    } catch (error) {
      console.error('Failed to load sources:', error);
    } finally {
      setLoadingSources(false);
    }
  }, [resumeId]);

  useEffect(() => {
    if (!showAiInput) return;

    const handlePointerDown = (event: MouseEvent) => {
      // Never yank the panel away mid-run: the progress checklist is the only
      // place the agent's reasoning is shown, and a run in flight has nowhere
      // else to report to.
      if (isInstructionVoiceRecording || isInstructionVoiceTranscribing || isImproving) return;
      const target = event.target as Node | null;
      if (!target) return;
      if (!containerRef.current?.contains(target)) {
        setShowAiInput(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (isInstructionVoiceRecording || isInstructionVoiceTranscribing || isImproving) return;
      if (event.key === 'Escape') {
        setShowAiInput(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showAiInput, isInstructionVoiceRecording, isInstructionVoiceTranscribing, isImproving]);

  useEffect(() => {
    if (!showRefineDialog) return;
    setShowAiInput(false);
    void refreshSources();
  }, [showRefineDialog, refreshSources]);

  useEffect(() => {
    if (!showAiInput) return;
    wasAiInputOpenRef.current = true;
    instructionInputRef.current?.focus();
  }, [showAiInput]);

  useEffect(() => {
    if (showAiInput) return;

    if (instructionVoiceRecorderRef.current?.state === 'recording') {
      cancelInstructionVoiceRef.current = true;
      instructionVoiceRecorderRef.current.stop();
    }
    setIsInstructionVoiceRecording(false);
    // Closing the panel discards the finished run, so reopening starts from a
    // clean checklist rather than the previous field's trace. Guarded on having
    // actually been open, so mounting the control does not churn a render.
    if (!wasAiInputOpenRef.current) return;
    wasAiInputOpenRef.current = false;
    resetImprove();
  }, [showAiInput, resetImprove]);

  useEffect(() => {
    return () => {
      if (instructionVoiceRecorderRef.current?.state === 'recording') {
        cancelInstructionVoiceRef.current = true;
        submitAfterVoiceRef.current = false;
        instructionVoiceRecorderRef.current.stop();
      }
      if (instructionVoiceStreamRef.current) {
        instructionVoiceStreamRef.current.getTracks().forEach((track) => track.stop());
        instructionVoiceStreamRef.current = null;
      }
    };
  }, []);

  const handleImprove = async (instructions?: string) => {
    if (!safeText.trim() || isImproving) return;

    // The panel has to be open for the progress checklist to have somewhere to
    // render — a voice submission can reach this without it.
    setShowAiInput(true);
    setInstructionVoiceError(null);
    submitAfterVoiceRef.current = false;

    const result = await runImprove({
      text: safeText,
      field,
      instructions: instructions?.trim() || undefined,
      context,
    });

    // `run` resolves to null when the stream failed; the hook has already put
    // the message on the checklist, so the toast is only a nudge.
    if (!result) {
      toast({
        title: 'Improve failed',
        description: 'Could not improve this section right now. Please retry.',
        variant: 'destructive',
      });
      return;
    }

    const improvedText = result.improvedText?.trim() ?? '';
    if (!improvedText) {
      toast({
        title: 'Improve failed',
        description: 'No improved text was returned. Please retry.',
        variant: 'destructive',
      });
      return;
    }

    onImproved(improvedText);
    setInstructionInput('');

    if (improvedText === safeText.trim()) {
      toast({
        title: 'No visible changes',
        description: 'Try adding more specific instructions.',
      });
    }

    // Empty when the rewrite was only whitespace, which the toast above already
    // covers — no dialog for a change the user cannot see.
    setImproveChanges(
      compareResumeField({
        section: IMPROVE_FIELD_SECTIONS[field],
        label: improveComparisonLabel,
        before: safeText,
        after: improvedText,
      })
    );

    // The panel deliberately stays open on success. The finished checklist —
    // and the "Why" panel behind it — is how a user sees what the agent
    // changed and, more importantly, what it declined to invent.
  };

  const stopInstructionVoiceRecording = (cancel: boolean) => {
    cancelInstructionVoiceRef.current = cancel;
    if (instructionVoiceRecorderRef.current?.state === 'recording') {
      instructionVoiceRecorderRef.current.stop();
    }
    if (cancel) {
      setIsInstructionVoiceRecording(false);
      instructionVoiceChunksRef.current = [];
      if (instructionVoiceStreamRef.current) {
        instructionVoiceStreamRef.current.getTracks().forEach((track) => track.stop());
        instructionVoiceStreamRef.current = null;
      }
    }
  };

  const handleStartInstructionVoiceRecording = async () => {
    if (!showAiInput || isInstructionVoiceRecording || isInstructionVoiceTranscribing || isImproving) {
      return;
    }

    instructionVoiceChunksRef.current = [];
    cancelInstructionVoiceRef.current = false;
    setInstructionVoiceError(null);

    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setInstructionVoiceError('Voice recording is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      instructionVoiceStreamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      instructionVoiceRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          instructionVoiceChunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        setIsInstructionVoiceRecording(false);
        stream.getTracks().forEach((track) => track.stop());
        instructionVoiceStreamRef.current = null;

        if (cancelInstructionVoiceRef.current) {
          cancelInstructionVoiceRef.current = false;
          submitAfterVoiceRef.current = false;
          instructionVoiceChunksRef.current = [];
          return;
        }

        const blob = new Blob(instructionVoiceChunksRef.current, {
          type: recorder.mimeType || 'audio/webm',
        });
        instructionVoiceChunksRef.current = [];

        if (!blob.size) {
          return;
        }

        try {
          setIsInstructionVoiceTranscribing(true);
          const payload = new FormData();
          payload.append('audio', blob, 'resume-field-improve-voice.webm');
          const response = await fetch('/api/voice/chat', {
            method: 'POST',
            body: payload,
          });

          if (!response.ok) {
            throw new Error(await response.text());
          }

          const data = (await response.json()) as { transcript?: string };
          const transcript = data.transcript?.trim();
          if (!transcript) {
            throw new Error('No transcript returned');
          }

          const mergedInstructions = instructionInput.trim()
            ? `${instructionInput.trim()}\n${transcript}`
            : transcript;

          setInstructionInput(mergedInstructions);
          setInstructionVoiceError(null);

          if (submitAfterVoiceRef.current) {
            submitAfterVoiceRef.current = false;
            await handleImprove(mergedInstructions);
          } else {
            toast({
              title: 'Voice instructions added',
              description: 'Review or edit, then press send to apply.',
            });
          }
        } catch (error) {
          console.error('Failed to transcribe field improve voice input:', error);
          setInstructionVoiceError('Could not transcribe voice input. Please try again.');
          submitAfterVoiceRef.current = false;
          toast({
            title: 'Voice transcription failed',
            description: 'Please retry recording and speak clearly.',
            variant: 'destructive',
          });
        } finally {
          setIsInstructionVoiceTranscribing(false);
        }
      };

      recorder.start();
      setIsInstructionVoiceRecording(true);
    } catch (error) {
      console.error('Failed to start field improve voice recording:', error);
      setInstructionVoiceError('Unable to access microphone. Please check permissions.');
      toast({
        title: 'Microphone unavailable',
        description: 'Please allow microphone access and try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSubmitSource = async (sourceData: Record<string, unknown>): Promise<boolean> => {
    if (!resumeId) return false;

    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sourceData),
      });

      if (!response.ok) return false;

      const data = await response.json();
      setSources((prev) => [data.source, ...prev]);
      return true;
    } catch (error) {
      console.error('Failed to add source:', error);
      return false;
    }
  };

  const handleDeleteSource = async (id: string) => {
    if (!resumeId) {
      throw new Error('Resume ID is required to delete a source.');
    }

    try {
      const response = await fetch(
        `/api/resume-builder/${resumeId}/sources?reportSourceId=${encodeURIComponent(id)}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(payload?.error || 'Failed to delete source');
      }

      setSources((prev) => prev.filter((s) => s.id !== id));
    } catch (error) {
      console.error('Failed to delete source:', error);
      throw error;
    }
  };

  const handleClearAllSources = async () => {
    if (!resumeId || sources.length === 0) return;

    setIsClearingSources(true);
    try {
      const results = await Promise.allSettled(
        sources.map((source) =>
          fetch(
            `/api/resume-builder/${resumeId}/sources?reportSourceId=${encodeURIComponent(source.id)}`,
            { method: 'DELETE' }
          )
        )
      );

      const failedCount = results.filter(
        (result) => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.ok)
      ).length;

      if (failedCount === 0) {
        setSources([]);
        return;
      }

      await refreshSources();
      toast({
        title: 'Could not clear all sources',
        description: `${failedCount} source${failedCount === 1 ? '' : 's'} could not be removed. Please retry.`,
        variant: 'destructive',
      });
    } catch (error) {
      console.error('Failed to clear sources:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while clearing sources.',
        variant: 'destructive',
      });
    } finally {
      setIsClearingSources(false);
    }
  };

  const runFieldRefine = async (): Promise<boolean> => {
    if (!resumeId || !safeText.trim()) return false;

    if (sources.length === 0) {
      toast({
        title: 'No sources',
        description: 'Add sources first to refine this field.',
        variant: 'destructive',
      });
      return false;
    }

    setIsRefining(true);
    try {
      const response = await fetch(`/api/resume-builder/${resumeId}/refine-field`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          field,
          text: safeText,
          context,
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to refine field' }));
        toast({
          title: 'Error',
          description: error.error || 'Failed to refine field',
          variant: 'destructive',
        });
        return false;
      }

      const data = await response.json();
      onImproved(data.improvedText);
      // The dialog closes itself on a truthy result.
      toast({
        title: 'Success',
        description: 'Field refined using attached sources.',
      });
      return true;
    } catch (error) {
      console.error('Failed to refine field:', error);
      toast({
        title: 'Error',
        description: 'An error occurred while refining this field.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsRefining(false);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            if (guestMode) { onGuestAction?.(); return; }
            setShowRefineDialog(true);
          }}
          disabled={!safeText.trim() || isImproving || isRefining}
        >
          <Wand2 className="mr-1 h-3 w-3" />
          REFINE
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            if (guestMode) { onGuestAction?.(); return; }
            setInstructionVoiceError(null);
            setShowAiInput((prev) => !prev);
          }}
          disabled={!safeText.trim() || isImproving || isRefining}
        >
          <PenLine className="mr-1 h-3 w-3" />
          IMPROVE
        </Button>
      </div>

      {showAiInput && (
        <div className="absolute right-0 top-full z-20 mt-2">
          <div className="space-y-1">
            <div className="relative flex items-center">
              <PenLine className="pointer-events-none absolute left-2.5 h-3 w-3 text-muted-foreground/60" />
              <input
                ref={instructionInputRef}
                value={instructionInput}
                onChange={(event) => setInstructionInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !isImproving && !isInstructionVoiceTranscribing) {
                    event.preventDefault();
                    void handleImprove(instructionInput.trim() || undefined);
                  }
                }}
                placeholder={
                  isInstructionVoiceRecording
                    ? 'Recording... keep speaking'
                    : isInstructionVoiceTranscribing
                      ? 'Transcribing voice instructions...'
                      : (instructionPlaceholder ?? '')
                }
                disabled={isImproving || isInstructionVoiceRecording || isInstructionVoiceTranscribing}
                autoFocus
                className="h-9 w-72 rounded-sm border-[3px] border-primary bg-background pl-8 pr-14 text-caption placeholder:text-muted-foreground/50 focus:outline-none focus:ring-0 disabled:opacity-100"
              />
              <button
                type="button"
                onClick={
                  isInstructionVoiceRecording
                    ? () => {
                        submitAfterVoiceRef.current = false;
                        stopInstructionVoiceRecording(false);
                      }
                    : () => {
                        submitAfterVoiceRef.current = false;
                        void handleStartInstructionVoiceRecording();
                      }
                }
                disabled={isImproving || isInstructionVoiceTranscribing}
                className="absolute right-7 flex h-5 w-5 items-center justify-center rounded-sm border border-border bg-background text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-100"
                aria-label={isInstructionVoiceRecording ? 'Stop voice recording' : 'Start voice recording'}
              >
                {isInstructionVoiceTranscribing ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : isInstructionVoiceRecording ? (
                  <Square className="h-2.5 w-2.5" />
                ) : (
                  <Mic className="h-2.5 w-2.5" />
                )}
              </button>
              <button
                type="button"
                onClick={() => {
                  if (isInstructionVoiceRecording) {
                    submitAfterVoiceRef.current = true;
                    stopInstructionVoiceRecording(false);
                    return;
                  }
                  void handleImprove(instructionInput.trim() || undefined);
                }}
                disabled={isImproving || isInstructionVoiceTranscribing}
                className="absolute right-1.5 flex h-5 w-5 items-center justify-center rounded-sm bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-100"
                aria-label="Send improve instructions"
              >
                {isImproving ? (
                  <div className="h-2.5 w-2.5 animate-spin rounded-full border border-current border-t-transparent" />
                ) : (
                  <ArrowUp className="h-2.5 w-2.5" />
                )}
              </button>
            </div>
            {instructionVoiceError && (
              <p className="max-w-72 text-caption text-destructive">{instructionVoiceError}</p>
            )}
            {improve.status !== 'idle' && (
              <ResumeImproveProgress
                steps={improve.steps}
                status={improve.status}
                error={improve.error}
                trace={improve.trace}
                className="w-72 bg-background"
              />
            )}
          </div>
        </div>
      )}

      {/*
        Same dialog the whole-resume refine uses, so "refine" means one thing
        everywhere. Only the header copy differs: this run rewrites a single
        field, not the document.
      */}
      <RefineJobSourceDialog
        open={showRefineDialog}
        onOpenChange={setShowRefineDialog}
        onAddSource={handleSubmitSource}
        onRefine={runFieldRefine}
        isRefining={isRefining}
        title="Refine Field"
        description="Refine rewrites just this field against a target job. Add that job below, then run refine."
        confirmLabel="Refine field"
      />

      {resumeId && (
        <AddSourceDialog
          open={showAddSourceDialog}
          onOpenChange={setShowAddSourceDialog}
          onAdd={handleSubmitSource}
        />
      )}

      <ResumeComparisonDialog
        open={improveChanges.length > 0}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setImproveChanges([]);
        }}
        title="Improved text"
        description="This is what the improve pass changed. The new text is already in the editor — undo it there if you prefer the original."
        changes={improveChanges}
        closeLabel="Keep it"
      />
    </div>
  );
}
